import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { IError } from "types/api";
import { IAccessTokenBody, IAccountState, ILogin, ILoginForm, IUser } from "types/models/Account";
import { RootState } from "types/reducer";

const initialState: IAccountState = {
  isAuth: false,
  isInitial: false,
  isFetching: false,
  isFetched: false,
  isSignup: false,
  isForgot: false,
  user: undefined,
  errors: [],
  sessionOut: false
};

const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    initial(state, action: PayloadAction<Partial<IAccountState>>) {
      state.isAuth = Boolean(action.payload.isAuth);
      state.isInitial = true;
      state.isFetching = false;
      state.user = undefined;
      state.errors = [];
    },
    accessToken(state, action: PayloadAction<IAccessTokenBody>) {
      state.isInitial = false;
      state.isAuth = false;
      state.user = undefined;
      state.isFetching = true;
      state.errors = [];
    },
    login(state, action: PayloadAction<any>) {
      state.isAuth = false;
      state.isInitial = true;
      state.isFetching = true;
      state.isFetched = false;
      state.user = undefined;
      state.errors = [];
    },
    loginSuccess(state, action: PayloadAction<any>) {
      state.user = action.payload;
      state.isFetching = false;
      state.isFetched = true;
    },
    loginFailure(state, action: PayloadAction<IError[]>) {
      state.isAuth = false;
      state.isInitial = true;
      state.isFetching = false;
      state.isFetched = true;
      state.user = undefined;
      state.errors = action.payload;
    },
    user(state) {
      state.isAuth = false;
      state.isInitial = true;
      state.isFetching = true;
      state.isFetched = false;
      state.user = undefined;
      state.errors = [];
    },
    userSuccess(state, action: PayloadAction<any>) {
      state.user = action.payload;
      state.isFetching = false;
      state.isFetched = true;
    },
    userFailure(state, action: PayloadAction<IError[]>) {
      state.isAuth = false;
      state.isInitial = true;
      state.isFetching = false;
      state.isFetched = true;
      state.user = undefined;
      state.errors = action.payload;
    },
    signup(state, action: PayloadAction<ILogin>) {
      state.isFetching = true;
      state.isFetched = false;
    },
    signupSuccess(state) {
      state.isFetching = false;
    },
    signupFailure(state) {
      state.isFetching = false;
      state.isFetched = true;
    },
    forgot(state, action: PayloadAction<any>) {
      state.isFetching = true;
      state.isFetched = false;
    },
    forgotSuccess(state) {
      state.isFetching = false;
    },
    forgotFailure(state) {
      state.isFetching = false;
      state.isFetched = true;
    },
    resetPW(state, action: PayloadAction<any>) {
      state.isFetching = true;
      state.isFetched = false;
    },
    resetPWSuccess(state) {
      state.isFetching = false;
    },
    resetPWFailure(state) {
      state.isFetching = false;
      state.isFetched = true;
    },
    logout(state) {
    },
    sessionOut(state){
      state.sessionOut = true;
    },
    setIsSignupDone(state){
      state.isSignup = false;
    },
    setIsForgotDone(state){
      state.isForgot = false;
    }
  }
});

// Actions
export const initial = AuthSlice.actions.initial;
export const accessToken = AuthSlice.actions.accessToken;
export const login = AuthSlice.actions.login;
export const loginSuccess = AuthSlice.actions.loginSuccess;
export const loginFailure = AuthSlice.actions.loginFailure;
export const fetchUserInfo = AuthSlice.actions.user;
export const userSuccess = AuthSlice.actions.userSuccess;
export const userFailure = AuthSlice.actions.userFailure;
export const signup = AuthSlice.actions.signup;
export const signupSuccess = AuthSlice.actions.signupSuccess;
export const signupFailure = AuthSlice.actions.signupFailure;
export const forgot = AuthSlice.actions.forgot;
export const forgotSuccess = AuthSlice.actions.forgotSuccess;
export const forgotFailure = AuthSlice.actions.forgotFailure;
export const resetPW = AuthSlice.actions.resetPW;
export const resetPWSuccess = AuthSlice.actions.resetPWSuccess;
export const resetPWFailure = AuthSlice.actions.resetPWFailure;
export const logout = AuthSlice.actions.logout;
export const sessionOut = AuthSlice.actions.sessionOut;
export const setIsSignupDone = AuthSlice.actions.setIsSignupDone;
export const setIsForgotDone = AuthSlice.actions.setIsForgotDone;

// Selectors
export const getIsAuth = (state: RootState) => state.auth.isAuth;
export const getIsInitial = (state: RootState) => state.auth.isInitial;
export const getIsFetching = (state: RootState) => state.auth.isFetching;
export const getIsFetched = (state: RootState) => state.auth.isFetched;
export const getIsSignup = (state: RootState) => state.auth.isSignup;
export const getIsForgot = (state: RootState) => state.auth.isForgot;
export const getCurrentUser = (state: RootState) => state.auth.user;
export const getAuthErrors = (state: RootState) => state.auth.errors;
export const getSesstionStatus = (state: RootState) => state.auth.sessionOut;
export const getUserInfo = (state: RootState) => state.auth.user;
export const getActiveUser = (state: RootState) => state.auth.user?.active;
export const getUserPermission = (state: RootState) => state.auth.user?.permission;
export const getUserDepartment = (state: RootState) => state.auth.user?.department;

// Reducer
const AuthReducer = AuthSlice.reducer;
export default AuthReducer;
